import * as _ from "lodash";
import * as React from "react";
import * as semver from "semver";
import * as S from "@styled";
import { setLastReadVersion } from "navigation/actions/AppActions";
import { CustomPopoverBase, ICustomPopoverProps } from "shared/components/Popover";
import { Utilities } from "../../shared/utilities";
import { AppContext, releaseNoteStorageKey } from "../App";
import { IAppReducerState } from "../shared/model";
import NotificationMessageNotifier from "./NotificationMessageNotifier";
import NotificationBell from "./NotificationBell";
import styled from "styled-components";
import useUserTheme from "shared/styles/hooks/useUserTheme";
import theme from "styled-theming";
import { ThemeType } from "shared/styles/theme";

const releaseNotesColor = theme("mode", {
    [ThemeType.light]: S.Colors.grey_1,
    [ThemeType.dark]: S.Colors.grey_5
})

const StyledReleaseNoteLink = styled.a`
    color: ${releaseNotesColor};
    text-decoration: underline;
    font-size: 0.938rem;
    line-height: 18px;
    padding-bottom: 5px;
    text-underline-offset: 2.5px;
`

export const NotificationCenterCustomPopover: React.FC<React.PropsWithChildren<ICustomPopoverProps>> = (props) => {
    const [isPopoverOpen, setIsPopoverOpen] = React.useState<boolean>(false);

    return (
        <CustomPopoverBase
            {...props}
            isOpen={isPopoverOpen}
            onClickOutside={() => setIsPopoverOpen(false)}
            onChildrenClick={() => setIsPopoverOpen(prev => !prev)}
        />
    );
}

const NotificationCenter: React.FC = () => {
    const { appState, dispatch } = React.useContext<IAppReducerState>(AppContext);
    const [theme] = useUserTheme();
    const isLightMode = theme === ThemeType.light;
    const [releaseNote, setReleaseNote] = React.useState<IReleaseNote>();

    React.useEffect(() => {
        const fetchCoreVersion = async () => {
            try {
                const splitVersion: string[] = appState.coreVersion.split(".").slice(0, -1); // remove pre-release number
                const semanticJoin = splitVersion.join("."); // 5.4.2
                const urlJoin = splitVersion.join(""); // 542
                setReleaseNote({
                    version: semanticJoin,
                    intercomLink: `https://docs.frends.com/en/articles/10154013-6-0-release-notes#${urlJoin}`
                });
            } catch (e) {
                console.error(e);
                Utilities.toastError(e);
            }
        }
        fetchCoreVersion();
    }, [appState.coreVersion])

    const setLasReadVersion = () => setLastReadVersion(dispatch, (releaseNote?.version ?? null), releaseNoteStorageKey); // null-coalesce returns undefined, but setLastReadVersion expect string | null

    const toolTip = (
        <S.FlexContainer asColumn={true}>
            <NotificationMessageNotifier
                // appState and dispatch can't be read in NotificationMessageNotifier cause it's not a direct child, so that's why we pass them as props
                state={{ appState, dispatch }}
            />
            <S.Label style={{ color: isLightMode ? S.Colors.grey_1 : S.Colors.grey_5 }}>Release notes</S.Label>
            <StyledReleaseNoteLink href={releaseNote?.intercomLink} target="_blank" >
                Release notes for {semver.valid(semver.coerce(releaseNote?.version))} <i className="ci-external_link"></i>
            </StyledReleaseNoteLink>
            <StyledReleaseNoteLink href="https://docs.frends.com/en/collections/1192384-release-notes" target="_blank" >
                All release notes <i className="ci-external_link"></i>
            </StyledReleaseNoteLink>
        </S.FlexContainer>
    );

    const animateNotificationBell = React.useMemo(() => {
        return (!_.isEmpty(appState?.notificationMessages) && appState?.notificationMessages.some((s) => s.viewed !== true) ||
            appState?.lastReadVersion !== "" && !_.isEqual(appState?.lastReadVersion, releaseNote?.version));
    }, [appState, releaseNote]);

    return (
        <li role="presentation" id="navigation-notification" className="pointer">
            <NotificationCenterCustomPopover
                tooltip={toolTip}
                clickHandlerStyles={{ cursor: "pointer" }}
                position={["bottom"]}
                popoverContentStyles={{ background: isLightMode ? S.Colors.grey_5 : S.Colors.dark5, border: `1px solid ${S.Colors.grey_2}`, borderRadius: "15px", minWidth: "400px", maxWidth: "500px" }}
                arrowColor={S.Colors.grey_2}
                onOpenHandler={setLasReadVersion}
            >
                <NotificationBell animate={animateNotificationBell} />
            </NotificationCenterCustomPopover>
        </li>
    );
};

interface IReleaseNote {
    version: string;
    intercomLink: string;
}

export default NotificationCenter;
